import api from '@/api';
import { mapFromApi, mapToApi } from '@/mappers';
import type { Applicant, ApplicationDraft, AsyncResult, CompletedLoanApplication, LoanApplication } from '@/types';
import axios from 'axios';
import { computed, ref } from 'vue';
import type { ApplicationDto } from '@/api/backend/users/applications';
import { httpApi } from '@/api/backend/_http';
import { useCookies } from '@vueuse/integrations/useCookies';
import { formatAddress } from '@/helpers/format';

import type { BrokerRetrieveApplicationDraftDto } from '@/api/backend/brokers/applications';
import type { UnaccreditedBrokerProfile } from '@/types/UnaccreditedBrokerProfile';
import type { SectionStates } from '@/types/SectionState';
import type { LoanAppMachineContext } from './loanAppMachine';
import type { BrokerLoanAppMachineContext } from './loanBrokerAppMachine';
import type { ErhlLoanAppMachineContext } from '@/helpers/loanErhlAppMachine';
import { INVALID_JWT_ERROR } from '@/helpers/useSessionExpiry';

type CustomerDraftPayload = ApplicationDraft & { applicantEmails?: string[]; applicantPhoneNumbers?: string[] };
type CustomerCreateDraftResponse = { applicationDraftId: string; signedUrl: string };
type CustomerUpdateDraftResponse = { signedUrl: string };
type CustomerRetrieveDraftResponse = { applicationDraft: BrokerRetrieveApplicationDraftDto };

class ConversionException extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConversionException';
  }
}

export function useApplicationApi() {
  const isProcessing = ref(0);
  const cookies = useCookies();

  const submitApplication = async (options: { loanApp: CompletedLoanApplication }): AsyncResult<string> => {
    const { loanApp } = options;
    isProcessing.value++;
    try {
      const request = mapToApi.createApplication(loanApp);
      const resolvedBrokerId = loanApp.brokerId || cookies.get('broker-id');
      loanApp.userType === 'Broker'
        ? await api.backend.brokers.applications.submitApplication(
            { ...request, brokerId: resolvedBrokerId },
            loanApp.structuralVersionNumber,
          )
        : await api.backend.users.applications.submitApplication(request, loanApp.structuralVersionNumber);
      return { success: true, value: 'Application submitted' };
    } catch (error) {
      console.error(error);
      return { success: false, error: api.getErrorMsg(error) };
    } finally {
      isProcessing.value--;
    }
  };

  const submitScenario = async (options: {
    loanApp: CompletedLoanApplication;
    sectionStates: SectionStates;
    unaccreditedBroker?: UnaccreditedBrokerProfile;
  }): AsyncResult<string> => {
    const { loanApp, unaccreditedBroker, sectionStates } = options;
    isProcessing.value++;
    try {
      const request = mapToApi.createApplication(loanApp);
      const resolvedBrokerId = loanApp.brokerId || cookies.get('broker-id');
      if (loanApp.userType === 'Broker') {
        await api.backend.brokers.applications.submitApplication(
          { ...request, sectionStates, brokerId: resolvedBrokerId },
          loanApp.structuralVersionNumber,
        );
      } else if (loanApp.userType === 'Crm') {
        await api.backend.crm.applications.submitApplication({ ...request, sectionStates });
      } else {
        await api.backend.users.applications.submitApplication(
          {
            ...request,
            sectionStates,
            ...(loanApp.userType === 'UnaccreditedBroker' && unaccreditedBroker && { unaccreditedBroker }),
          },
          loanApp.structuralVersionNumber,
        );
      }

      return { success: true, value: 'Scenario submitted' };
    } catch (error) {
      console.error(error);
      return { success: false, error: api.getErrorMsg(error) };
    } finally {
      isProcessing.value--;
    }
  };

  const submitApplicant = async (options: {
    applicant: Applicant;
    loanApp: CompletedLoanApplication;
  }): AsyncResult<string> => {
    const { applicant, loanApp } = options;
    isProcessing.value++;
    try {
      const request =
        loanApp.userType === 'Broker'
          ? mapToApi.updateApplication(loanApp)
          : mapToApi.updateApplication(loanApp, applicant || loanApp.applicants[0]);
      const response =
        loanApp.userType === 'Broker'
          ? await api.backend.brokers.applications.submitApplicants({ applicationId: loanApp.applicationId }, request)
          : await api.backend.users.applications.submitApplicant(
              { applicationId: loanApp.applicationId },
              request,
              loanApp.structuralVersionNumber,
            );
      return { success: true, value: response.message };
    } catch (error) {
      console.error(error);
      return { success: false, error: api.getErrorMsg(error) };
    } finally {
      isProcessing.value--;
    }
  };

  const submitApplicants = async (options: {
    applicant: Applicant;
    loanApp: CompletedLoanApplication;
  }): AsyncResult<string> => {
    const { loanApp } = options;
    isProcessing.value++;
    try {
      const request = mapToApi.updateApplication(loanApp);
      const response =
        loanApp.userType === 'Broker'
          ? await api.backend.brokers.applications.submitApplicants({ applicationId: loanApp.applicationId }, request)
          : await api.backend.users.applications.submitApplicants(
              { applicationId: loanApp.applicationId },
              request,
              loanApp.structuralVersionNumber,
            );
      return { success: true, value: response.message };
    } catch (error) {
      console.error(error);
      return { success: false, error: api.getErrorMsg(error) };
    } finally {
      isProcessing.value--;
    }
  };

  const retrieveApplication = async (
    appId?: string,
  ): Promise<{
    success: boolean;
    error?: string;
    value?: LoanApplication;
  }> => {
    isProcessing.value++;
    try {
      const response = await api.backend.users.applications.retrieveApplication({ id: appId });
      const application = response.data.application as ApplicationDto;
      const secondaryApplicant = application.applicants?.find((applicant) => applicant.applicantType === 'secondary');
      const secondaryApplicantDeclared = secondaryApplicant?.isApplicantDeclared ?? false;
      try {
        const loanApplication = mapFromApi.application(application);
        loanApplication.isResubmission = true;
        return !secondaryApplicantDeclared
          ? { success: true, value: loanApplication }
          : { success: false, error: 'expired-link' };
      } catch (e) {
        throw new ConversionException('Cannot convert payload into application object');
      }
    } catch (error) {
      if (error instanceof ConversionException) {
        return { success: false, error: 'conversion-fail' };
      } else {
        return { success: false, error: 'network-error' };
      }
    } finally {
      isProcessing.value--;
    }
  };

  const retrieveBrokerApplication = async (
    appId: string,
  ): Promise<{
    success: boolean;
    error?: string;
    value?: LoanApplication;
  }> => {
    isProcessing.value++;
    try {
      const response = await api.backend.brokers.applications.retrieveBrokerApplication({ id: appId });
      const application = response.data.application as ApplicationDto;
      try {
        const loanApplication = mapFromApi.application(application);
        loanApplication.isResubmission = true;
        return { success: true, value: loanApplication };
      } catch (e) {
        throw new ConversionException('Cannot convert payload into application object');
      }
    } catch (error) {
      if (error instanceof ConversionException) {
        return { success: false, error: 'conversion-fail' };
      } else {
        return { success: false, error: 'network-error' };
      }
    } finally {
      isProcessing.value--;
    }
  };

  const backupApplication = async (data: LoanApplication, overrideEmail?: string) => {
    try {
      const email = overrideEmail || data.applicants[0]?.email || '';
      const payload = {
        ...mapToApi.createApplication(data),
        brokerId: data.brokerId ?? '',
        submissionDate: new Date(),
      };
      const json = JSON.stringify(payload);
      const blob = new Blob([json], { type: 'application/json' });
      const file = new File([blob], email, { type: 'application/json' });
      const response = await api.backend.public.backupApplication({
        email,
        contentLength: file.size,
      });
      if (response.data.signedUrl) {
        const signedUrl = response.data.signedUrl;
        if (signedUrl !== 'string') {
          await axios.put(signedUrl, file, { headers: { 'Content-Type': file.type } });
        }
      }
      return response;
    } catch (error) {
      console.error(error);
    }
  };

  const backupQuote = async (
    data: LoanApplication,
    sectionStates: SectionStates,
    overrideEmail?: string,
    unaccreditedBroker?: UnaccreditedBrokerProfile,
  ) => {
    try {
      const email = overrideEmail || data.applicants[0]?.email || '';
      const payload = {
        ...mapToApi.createApplication(data),
        sectionStates,
        ...(unaccreditedBroker && { unaccreditedBroker }),
        brokerId: data.brokerId ?? '',
        submissionDate: new Date(),
      };
      const json = JSON.stringify(payload);
      const blob = new Blob([json], { type: 'application/json' });
      const file = new File([blob], email, { type: 'application/json' });
      const response = await api.backend.public.backupApplication({
        email,
        contentLength: file.size,
      });
      if (response.data.signedUrl) {
        const signedUrl = response.data.signedUrl;
        if (signedUrl !== 'string') {
          await axios.put(signedUrl, file, { headers: { 'Content-Type': file.type } });
        }
      }
      return response;
    } catch (error) {
      console.error(error);
    }
  };

  const retrieveApplicationDraft = async (
    applicationDraftId?: string,
  ): Promise<{
    success: boolean;
    error?: string;
    value?: BrokerRetrieveApplicationDraftDto;
  }> => {
    isProcessing.value++;
    try {
      const response = await api.backend.brokers.applications.retrieveBrokerApplicationDraft({
        id: applicationDraftId,
      });
      return { success: true, value: response.data.applicationDraft };
    } catch (error) {
      return { success: false, error: 'network-error' };
    } finally {
      isProcessing.value--;
    }
  };

  const saveApplicationToDraft = async (
    context: LoanAppMachineContext | BrokerLoanAppMachineContext | ErhlLoanAppMachineContext,
    loanApp: LoanApplication,
    completedStep?: string,
  ): Promise<string | undefined> => {
    isProcessing.value++;
    const state = {
      state: {
        ...context,
        loanApp: {
          ...loanApp,
        },
      },
    };
    let applicationDraftId = loanApp.applicationDraftId;
    const json = JSON.stringify(state);
    const blob = new Blob([json], { type: 'application/json' });
    const file = new File([blob], `draft-${loanApp.brokerEmail ?? loanApp.applicants?.[0]?.email ?? ''}`, {
      type: 'application/json',
    });

    const applicantFullNames = (loanApp?.applicants ?? []).map((item) =>
      [item.firstName, item.lastName].filter(Boolean).join(' '),
    );
    const applicantEmails = (loanApp?.applicants ?? [])
      .map((item) => item.email)
      .filter((email): email is string => Boolean(email));
    const applicantPhoneNumbers = (loanApp?.applicants ?? [])
      .map((item) => item.mobile)
      .filter((mobile): mobile is string => Boolean(mobile));
    const incomingPropertyAddresses = (loanApp.incomingProperties ?? []).map((item) => formatAddress(item.address));
    const outgoingPropertyAddresses = (loanApp.outgoingProperties ?? []).map((item) => formatAddress(item.address));

    const isCustomer = !loanApp.brokerId;

    let signedUrlResult = '';
    try {
      if (isCustomer) {
        const customerPayload: CustomerDraftPayload = {
          applicantFullNames,
          applicantEmails,
          applicantPhoneNumbers,
          incomingPropertyAddresses,
          outgoingPropertyAddresses,
        };
        if (!applicationDraftId) {
          const response = await httpApi.post<CustomerCreateDraftResponse>(
            '/public/application-drafts/create-application-draft',
            { ...customerPayload, contentLength: file.size, ...(completedStep && { completedStep }) },
          );
          applicationDraftId = response.data.applicationDraftId;
          signedUrlResult = response.data.signedUrl;
        } else {
          const response = await httpApi.patch<CustomerUpdateDraftResponse>(
            '/public/application-drafts/update-application-draft',
            { ...customerPayload, contentLength: file.size, ...(completedStep && { completedStep }) },
            { params: { id: applicationDraftId } },
          );
          signedUrlResult = response.data.signedUrl;
        }
      } else {
        const payload: ApplicationDraft & { applicantEmails?: string[]; applicantPhoneNumbers?: string[] } = {
          brokerId: loanApp.brokerId,
          scenarioApplicationId: loanApp.scenarioApplicationId ? loanApp.scenarioApplicationId : undefined,
          applicantFullNames,
          applicantEmails,
          applicantPhoneNumbers,
          incomingPropertyAddresses,
          outgoingPropertyAddresses,
        };
        if (!applicationDraftId) {
          const response = await api.backend.brokers.applications.brokerCreateApplicationDraft({
            ...payload,
            contentLength: file.size,
            ...(completedStep && { completedStep }),
          } as Parameters<typeof api.backend.brokers.applications.brokerCreateApplicationDraft>[0]);
          applicationDraftId = response.data.applicationDraftId;
          signedUrlResult = response.data.signedUrl;
        } else {
          const response = await api.backend.brokers.applications.brokerUpdateApplicationDraft(
            { id: applicationDraftId },
            {
              ...payload,
              contentLength: file.size,
              ...(completedStep && { completedStep }),
            } as Parameters<typeof api.backend.brokers.applications.brokerUpdateApplicationDraft>[1],
          );
          signedUrlResult = response.data.signedUrl;
        }
      }
      if (signedUrlResult !== 'string') {
        await axios.put(signedUrlResult, file, { headers: { 'Content-Type': file.type } });
      }
      return applicationDraftId;
    } catch (error) {
      if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
        throw error;
      }
      console.error(error);
    } finally {
      isProcessing.value--;
    }
  };

  const customerRetrieveApplicationDraft = async (
    applicationDraftId: string,
  ): Promise<{ success: boolean; error?: string; value?: BrokerRetrieveApplicationDraftDto }> => {
    isProcessing.value++;
    try {
      const response = await httpApi.get<CustomerRetrieveDraftResponse>(
        '/public/application-drafts/retrieve-application-draft',
        { params: { id: applicationDraftId } },
      );
      return { success: true, value: response.data.applicationDraft };
    } catch (error) {
      return { success: false, error: 'network-error' };
    } finally {
      isProcessing.value--;
    }
  };

  return {
    isLoading: computed(() => isProcessing.value > 0),
    retrieveApplication,
    retrieveBrokerApplication,
    submitApplication,
    submitApplicant,
    submitApplicants,
    backupApplication,
    backupQuote,
    saveApplicationToDraft,
    retrieveApplicationDraft,
    customerRetrieveApplicationDraft,
    submitScenario,
  };
}
